/**
 * `SkinParams` helpers: defaults, immutable edits, Left/Right mirroring, hierarchy lookups, a
 * defensive normalizer for persisted / imported values, and `checkSkinModelScale` — the guard
 * that says out loud when a model's units are not the metres every length here assumes.
 *
 * Pure module — no DOM, no editor state. Every mutating helper returns a NEW params object so
 * the store can replace its slice immutably.
 */

import type {
  CanonicalBoneId,
  SkinJointParams,
  SkinParams,
  SkinRegionOverride,
  SkinResolution,
} from '../../shared/types';
import { CANONICAL_CHILDREN, CANONICAL_PARENT, isCanonicalBoneId } from '../../shared/canonicalRig';

const SKIN_RESOLUTIONS: readonly SkinResolution[] = [64, 96, 128, 192];

/** Sanity caps for the smoothing controls (the UI exposes a much smaller range). */
const MAX_SMOOTHING_ITERATIONS = 50;

/** Per-joint `offset` is clamped to ±10 cm in model space, everywhere it can be set. */
const MAX_JOINT_OFFSET = 0.1;

/**
 * `blend` is the width of the weight transition **in metres** (the τ of the solver's
 * `exp(-(d - dmin)/τ)` falloff), not a 0..1 ratio. Below 2 mm the boundary is a hard step on any
 * real mesh; above 20 cm every bone bleeds into every other one. Params persisted before this
 * change stored a 0..1 ratio and simply clamp into the new range on load.
 */
const MIN_BLEND = 0.002;
const MAX_BLEND = 0.2;

/**
 * Frozen defaults. Deep-frozen: the object, `joints` and `regionOverrides` are all immutable,
 * so accidental mutation throws in strict mode instead of corrupting every future project.
 * Get a mutable copy with `defaultSkinParams()`.
 */
export const DEFAULT_SKIN_PARAMS: Readonly<SkinParams> = Object.freeze({
  version: 1,
  resolution: 128,
  blend: 0.03,
  smoothingIterations: 2,
  smoothingLambda: 0.5,
  symmetry: true,
  joints: Object.freeze({}) as SkinParams['joints'],
  regionOverrides: Object.freeze([]) as unknown as SkinRegionOverride[],
} satisfies SkinParams);

/** Deep clone: `joints` entries and `regionOverrides` (including their vertex arrays) are fresh. */
export function cloneSkinParams(p: SkinParams): SkinParams {
  const joints: SkinParams['joints'] = {};
  for (const key of Object.keys(p.joints) as CanonicalBoneId[]) {
    const jp = p.joints[key];
    if (jp) joints[key] = cloneJointParams(jp);
  }
  return {
    version: p.version,
    resolution: p.resolution,
    blend: p.blend,
    smoothingIterations: p.smoothingIterations,
    smoothingLambda: p.smoothingLambda,
    symmetry: p.symmetry,
    joints,
    regionOverrides: p.regionOverrides.map(cloneOverride),
  };
}

/** A fresh, mutable copy of `DEFAULT_SKIN_PARAMS`. */
export function defaultSkinParams(): SkinParams {
  return cloneSkinParams(DEFAULT_SKIN_PARAMS as SkinParams);
}

/** `'LeftUpperArm'` ↔ `'RightUpperArm'`; ids without a side return themselves. */
export function mirrorBoneId(id: CanonicalBoneId): CanonicalBoneId {
  let swapped: string | null = null;
  if (id.startsWith('Left')) swapped = `Right${id.slice('Left'.length)}`;
  else if (id.startsWith('Right')) swapped = `Left${id.slice('Right'.length)}`;
  return swapped !== null && isCanonicalBoneId(swapped) ? swapped : id;
}

/**
 * Patch one joint's params. Returns a NEW params object; when `p.symmetry` is on, the same
 * patch is applied to `mirrorBoneId(id)` as well. A joint that has no entry yet starts from
 * `{ offset: 0 }`. Passing `blend: undefined` explicitly clears the per-joint override.
 *
 * `offset` is clamped to ±`MAX_JOINT_OFFSET` metres and `blend` to `MIN_BLEND`..`MAX_BLEND`
 * metres; a non-finite `offset` leaves the previous value alone and a non-finite `blend` clears
 * the per-joint override.
 */
export function setJointParams(
  p: SkinParams,
  id: CanonicalBoneId,
  patch: Partial<SkinJointParams>,
): SkinParams {
  const next = cloneSkinParams(p);
  applyJointPatch(next, id, patch);
  if (next.symmetry) {
    const mirrored = mirrorBoneId(id);
    if (mirrored !== id) applyJointPatch(next, mirrored, patch);
  }
  return next;
}

/**
 * Remove one joint's entry entirely (back to "no correction"). Returns a NEW params object;
 * when `p.symmetry` is on, the mirrored joint's entry is removed too. Removing a joint that has
 * no entry is a no-op that still returns a fresh object.
 */
export function clearJointParams(p: SkinParams, id: CanonicalBoneId): SkinParams {
  const next = cloneSkinParams(p);
  delete next.joints[id];
  if (next.symmetry) {
    const mirrored = mirrorBoneId(id);
    if (mirrored !== id) delete next.joints[mirrored];
  }
  return next;
}

/**
 * Effective params of one joint: `blend` (metres) falls back to the global `p.blend`. Defensive —
 * the same clamps the setters apply are re-applied here, so a hand-built params object can never
 * hand the solver an out-of-range offset or blend.
 */
export function jointParamsOf(p: SkinParams, id: CanonicalBoneId): Required<SkinJointParams> {
  const jp = p.joints[id];
  const blend = jp && jp.blend !== undefined && Number.isFinite(jp.blend) ? jp.blend : p.blend;
  return {
    offset: jp ? clampOffset(jp.offset) : 0,
    blend: Number.isFinite(blend) ? clampBlend(blend) : DEFAULT_SKIN_PARAMS.blend,
  };
}

/**
 * Clamp a blend width (metres) into the legal range, so every writer of `SkinParams.blend` —
 * the store action, an importer, a test surface — applies the same bounds the solver assumes.
 * A non-finite value falls back to the default width.
 */
export function clampSkinBlend(value: number): number {
  return clampBlend(value);
}

// ---------------------------------------------------------------------------
// Model scale — the unit assumption behind every number in this file
// ---------------------------------------------------------------------------

/**
 * Every length in `SkinParams` is in METRES, but the geodesic distances they are compared
 * against are in MODEL UNITS (`VertexDistanceCache.distances`). The two only line up when the
 * model itself is metre-scale, and nothing checked that (audit §2.4 #3).
 *
 * On a centimetre-scale GLB — the common case, since a Blender/Maya export in cm arrives 100x
 * too big — the default 3 cm blend width becomes 0.3 mm of model, so `falloff` in
 * `weightSolver.ts` flushes every neighbouring bone to zero and blending switches off entirely:
 * hard, faceted weight boundaries at every joint. The leak checker's 5 mm displacement threshold
 * shrinks the same way and starts reporting almost every vertex as a leak. Both failures look
 * like a bad model rather than a unit mismatch, which is why they need naming out loud.
 *
 * The parameters themselves are deliberately NOT rescaled here. `MAX_JOINT_OFFSET`, `MIN_BLEND`
 * and `MAX_BLEND` are applied by `normalizeSkinParams` to values already persisted in
 * `ProjectRecord.skinParams`; making them scale-relative changes what a saved project means and
 * needs a migration, not a one-line constant change. This module only tells the truth about the
 * mismatch so the user hears it before waiting on a solve.
 */

/** The model height the metre-based defaults were tuned against. */
const NOMINAL_HUMANOID_HEIGHT = 1.7;

/**
 * The range of model heights the metre-based defaults still behave sensibly over. Wide on
 * purpose: a 20 cm figurine and a 10 m giant are both legitimate art choices, while the unit
 * mistakes worth catching are off by 100x or 1000x, never by 3x.
 */
const MIN_PLAUSIBLE_HEIGHT = 0.2;
const MAX_PLAUSIBLE_HEIGHT = 10;

export type SkinModelUnit = 'metres' | 'centimetres' | 'millimetres' | 'unknown';

export interface SkinModelScaleCheck {
  /** The measurement the verdict came from, in model units. */
  height: number;
  /** `height / 1.7` — how many times larger the model is than a metre-scale humanoid. */
  scale: number;
  /** Best guess at the unit the model was authored in. */
  unit: SkinModelUnit;
  /** True when the metre-based skin params still mean at this scale what they say. */
  plausible: boolean;
  /** Stable code so a caller can route or de-duplicate the warning; `null` when plausible. */
  code: 'SKIN_MODEL_SCALE' | null;
  /** Warning naming the suspected unit AND the consequence; `null` when plausible. */
  warning: string | null;
}

/**
 * Judge whether a model is metre-scale, from its height in model units.
 *
 * Call this where auto-skinning starts — before voxelizing, so the user is told in front of the
 * wait rather than after it. Cheap, pure and total: a non-finite or non-positive height is
 * reported as unmeasurable rather than throwing, because a warning must never be the thing that
 * breaks a skinning run.
 */
export function checkSkinModelScale(height: number): SkinModelScaleCheck {
  if (!Number.isFinite(height) || height <= 0) {
    return {
      height,
      scale: Number.NaN,
      unit: 'unknown',
      plausible: false,
      code: 'SKIN_MODEL_SCALE',
      warning:
        'The model has no measurable height, so auto-skinning cannot tell whether it is in ' +
        'metres. Its blend width and joint offsets are metre values compared against distances ' +
        'in model units; if the model is not metre-scale, blending will be disabled and the ' +
        'leak check will report nearly every vertex.',
    };
  }

  const scale = height / NOMINAL_HUMANOID_HEIGHT;

  if (height >= MIN_PLAUSIBLE_HEIGHT && height <= MAX_PLAUSIBLE_HEIGHT) {
    return { height, scale, unit: 'metres', plausible: true, code: null, warning: null };
  }

  const unit = suspectedUnit(height);
  const blend = DEFAULT_SKIN_PARAMS.blend;
  // What the default blend width is worth as a fraction of the model — the number that decides
  // whether `falloff` can produce anything but 0 or 1 for a neighbouring bone.
  const blendAsFractionOfModel = blend / height;

  return {
    height,
    scale,
    unit,
    plausible: false,
    code: 'SKIN_MODEL_SCALE',
    warning:
      `This model is ${formatNumber(height)} units tall — ${formatNumber(scale)}x a metre-scale ` +
      `humanoid, so ${unitPhrase(unit)}. Auto-skinning measures its blend width ` +
      `(${blend} m by default) and joint offsets in METRES against distances in model units: at ` +
      `this scale the blend is only ${formatPercent(blendAsFractionOfModel)} of the model, so ` +
      'blending between neighbouring bones will be disabled and the leak check will flag almost ' +
      'every vertex. Re-import the model at metre scale, or raise the blend width to match these ' +
      'units.',
  };
}

/**
 * The bone itself, its parent and its children — the bones whose weights a per-joint edit can
 * touch. `Root` is never included (it carries no skin weight).
 */
export function hierarchyNeighbours(id: CanonicalBoneId): CanonicalBoneId[] {
  const out: CanonicalBoneId[] = [];
  if (id !== 'Root') out.push(id);
  const parent = CANONICAL_PARENT[id];
  if (parent && parent !== 'Root') out.push(parent);
  for (const child of CANONICAL_CHILDREN[id]) {
    if (child !== 'Root') out.push(child);
  }
  return out;
}

/** True when `p` is value-equal to the defaults (key order irrelevant). */
export function isDefaultSkinParams(p: SkinParams): boolean {
  const d = DEFAULT_SKIN_PARAMS;
  if (
    p.version !== d.version ||
    p.resolution !== d.resolution ||
    p.blend !== d.blend ||
    p.smoothingIterations !== d.smoothingIterations ||
    p.smoothingLambda !== d.smoothingLambda ||
    p.symmetry !== d.symmetry
  ) {
    return false;
  }

  const keys = Object.keys(p.joints) as CanonicalBoneId[];
  const defaultKeys = Object.keys(d.joints) as CanonicalBoneId[];
  if (keys.length !== defaultKeys.length) return false;
  for (const key of keys) {
    const a = p.joints[key];
    const b = d.joints[key];
    if (!a || !b || a.offset !== b.offset || a.blend !== b.blend) return false;
  }

  if (p.regionOverrides.length !== d.regionOverrides.length) return false;
  for (let i = 0; i < p.regionOverrides.length; i += 1) {
    const a = p.regionOverrides[i];
    const b = d.regionOverrides[i];
    if (a.meshName !== b.meshName || a.boneId !== b.boneId) return false;
    if (a.vertices.length !== b.vertices.length) return false;
    for (let v = 0; v < a.vertices.length; v += 1) {
      if (a.vertices[v] !== b.vertices[v]) return false;
    }
  }

  return true;
}

/**
 * Coerce an arbitrary (persisted, imported, hand-edited) value into valid `SkinParams`:
 * missing fields take the defaults, out-of-range numbers are clamped, unknown bone ids and
 * malformed region overrides are dropped. Never throws.
 */
export function normalizeSkinParams(input: unknown): SkinParams {
  const out = defaultSkinParams();
  try {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return out;
    const raw = input as Record<string, unknown>;

    out.resolution = nearestResolution(raw.resolution);
    out.blend = clampNumber(raw.blend, MIN_BLEND, MAX_BLEND, DEFAULT_SKIN_PARAMS.blend);
    out.smoothingIterations = Math.round(
      clampNumber(
        raw.smoothingIterations,
        0,
        MAX_SMOOTHING_ITERATIONS,
        DEFAULT_SKIN_PARAMS.smoothingIterations,
      ),
    );
    out.smoothingLambda = clampNumber(raw.smoothingLambda, 0, 1, DEFAULT_SKIN_PARAMS.smoothingLambda);
    out.symmetry =
      typeof raw.symmetry === 'boolean' ? raw.symmetry : DEFAULT_SKIN_PARAMS.symmetry;

    const joints = raw.joints;
    if (joints && typeof joints === 'object' && !Array.isArray(joints)) {
      for (const [key, value] of Object.entries(joints as Record<string, unknown>)) {
        if (!isCanonicalBoneId(key)) continue;
        const jp = normalizeJointParams(value);
        if (jp) out.joints[key] = jp;
      }
    }

    const overrides = raw.regionOverrides;
    if (Array.isArray(overrides)) {
      for (const entry of overrides) {
        const normalized = normalizeOverride(entry);
        if (normalized) out.regionOverrides.push(normalized);
      }
    }
  } catch {
    return defaultSkinParams();
  }
  return out;
}

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

/**
 * Which unit a too-large model was probably authored in. The bands are the human ones: a person
 * is 0.5–5 m, so 50–500 units reads as centimetres and 500–5000 as millimetres. Anything else is
 * not a unit mistake we recognise, and saying "unknown" is more useful than guessing wrong.
 */
function suspectedUnit(height: number): SkinModelUnit {
  if (height >= 50 && height < 500) return 'centimetres';
  if (height >= 500 && height <= 5000) return 'millimetres';
  return 'unknown';
}

function unitPhrase(unit: SkinModelUnit): string {
  if (unit === 'unknown') return 'it is almost certainly not in metres';
  return `the model may be in ${unit}`;
}

/** Short, stable formatting for the warning text — no locale separators, no exponent soup. */
function formatNumber(value: number): string {
  if (Math.abs(value) >= 100) return String(Math.round(value));
  return String(Math.round(value * 100) / 100);
}

function formatPercent(fraction: number): string {
  const percent = fraction * 100;
  if (percent < 0.01) return '<0.01%';
  return `${formatNumber(percent)}%`;
}

function cloneJointParams(jp: SkinJointParams): SkinJointParams {
  const copy: SkinJointParams = { offset: jp.offset };
  if (jp.blend !== undefined) copy.blend = jp.blend;
  return copy;
}

function cloneOverride(o: SkinRegionOverride): SkinRegionOverride {
  return { meshName: o.meshName, vertices: o.vertices.slice(), boneId: o.boneId };
}

function applyJointPatch(
  p: SkinParams,
  id: CanonicalBoneId,
  patch: Partial<SkinJointParams>,
): void {
  const base = p.joints[id] ?? { offset: 0 };
  const merged: SkinJointParams = { offset: clampOffset(base.offset) };
  if (patch.offset !== undefined && Number.isFinite(patch.offset)) {
    merged.offset = clampOffset(patch.offset);
  }
  if ('blend' in patch) {
    // an explicit undefined (or a non-finite number) clears the per-joint override
    if (patch.blend !== undefined && Number.isFinite(patch.blend)) {
      merged.blend = clampBlend(patch.blend);
    }
  } else if (base.blend !== undefined && Number.isFinite(base.blend)) {
    merged.blend = clampBlend(base.blend);
  }
  p.joints[id] = merged;
}

/** Per-joint offset invariant: finite and within ±`MAX_JOINT_OFFSET`. Non-finite → 0. */
function clampOffset(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(MAX_JOINT_OFFSET, Math.max(-MAX_JOINT_OFFSET, value));
}

/** Blend-width invariant: finite and within `MIN_BLEND`..`MAX_BLEND` metres. */
function clampBlend(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SKIN_PARAMS.blend;
  return Math.min(MAX_BLEND, Math.max(MIN_BLEND, value));
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

function nearestResolution(value: unknown): SkinResolution {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_SKIN_PARAMS.resolution;
  let best: SkinResolution = SKIN_RESOLUTIONS[0];
  let bestDist = Math.abs(value - best);
  for (const candidate of SKIN_RESOLUTIONS) {
    const dist = Math.abs(value - candidate);
    if (dist < bestDist) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}

function normalizeJointParams(value: unknown): SkinJointParams | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const offset = typeof raw.offset === 'number' ? clampOffset(raw.offset) : 0;
  const out: SkinJointParams = { offset };
  if (typeof raw.blend === 'number' && Number.isFinite(raw.blend)) {
    out.blend = clampBlend(raw.blend);
  }
  return out;
}

/** Arrays and typed arrays alike — persisted overrides may come back as either. */
function isIndexable(value: unknown): value is ArrayLike<unknown> {
  if (Array.isArray(value)) return true;
  if (!ArrayBuffer.isView(value) || value instanceof DataView) return false;
  return typeof (value as unknown as { length?: unknown }).length === 'number';
}

function normalizeOverride(value: unknown): SkinRegionOverride | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.meshName !== 'string' || raw.meshName.length === 0) return null;
  if (!isCanonicalBoneId(raw.boneId)) return null;
  const source = raw.vertices;
  if (!isIndexable(source)) return null;
  const vertices: number[] = [];
  for (let i = 0; i < source.length; i += 1) {
    const v = source[i];
    if (typeof v === 'number' && Number.isInteger(v) && v >= 0) vertices.push(v);
  }
  if (vertices.length === 0) return null;
  return { meshName: raw.meshName, vertices, boneId: raw.boneId };
}
